/**
 * audio-encoding.ts — AWS event-stream framing for Transcribe browser WebSocket.
 *
 * The Transcribe Streaming WebSocket protocol requires audio frames to be wrapped
 * in AWS event-stream binary framing before sending. This module provides that
 * encoding for browser-side use.
 *
 * Path choice: We use the raw WebSocket + manual event-stream framing path because:
 * - The server generates a SigV4 presigned URL (no AWS credentials in the browser)
 * - The @aws-sdk/client-transcribe-streaming SDK needs credentials at construction
 *   time, making it unsuitable for the presigned-URL approach
 * - The spike validated HTTP 101 with this exact URL format
 *
 * Event-stream binary frame format (AWS proprietary protocol):
 *   [total_length: 4 bytes BE] [headers_length: 4 bytes BE] [prelude_crc: 4 bytes BE]
 *   [headers: N bytes] [payload: M bytes] [message_crc: 4 bytes BE]
 *
 * For AudioEvent frames:
 *   headers: ':event-type' = 'AudioEvent', ':content-type' = 'application/octet-stream',
 *            ':message-type' = 'event'
 *   payload: raw PCM Int16 bytes
 *
 * CRC32 implementation: uses the standard IEEE polynomial (0xEDB88320).
 */

// ── CRC32 ─────────────────────────────────────────────────────────────────────

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c;
  }
  return table;
})();

function crc32(buf: Uint8Array): number {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < buf.length; i++) {
    crc = CRC_TABLE[(crc ^ buf[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

// ── Header encoding ───────────────────────────────────────────────────────────

/**
 * Encode a single event-stream header.
 * Format: [name_length: 1 byte] [name: N bytes] [value_type: 1 byte = 7 (string)]
 *         [value_length: 2 bytes BE] [value: M bytes]
 */
function encodeHeader(name: string, value: string): Uint8Array {
  const nameBytes = new TextEncoder().encode(name);
  const valueBytes = new TextEncoder().encode(value);
  const buf = new Uint8Array(1 + nameBytes.length + 1 + 2 + valueBytes.length);
  let offset = 0;
  buf[offset++] = nameBytes.length;
  buf.set(nameBytes, offset); offset += nameBytes.length;
  buf[offset++] = 7; // value type: string
  const dv = new DataView(buf.buffer);
  dv.setUint16(offset, valueBytes.length, false); offset += 2;
  buf.set(valueBytes, offset);
  return buf;
}

// ── wrapPcmInAudioEvent ───────────────────────────────────────────────────────

/**
 * Wraps a PCM Int16 ArrayBuffer in AWS event-stream AudioEvent framing.
 *
 * The resulting ArrayBuffer is ready to send directly over the Transcribe WebSocket:
 *   ws.send(wrapPcmInAudioEvent(pcmBuffer))
 *
 * @param pcmBuffer  ArrayBuffer containing raw Int16 PCM samples at 16kHz mono
 * @returns          ArrayBuffer with full event-stream framing
 */
export function wrapPcmInAudioEvent(pcmBuffer: ArrayBuffer): ArrayBuffer {
  // Build headers
  const h1 = encodeHeader(':event-type', 'AudioEvent');
  const h2 = encodeHeader(':content-type', 'application/octet-stream');
  const h3 = encodeHeader(':message-type', 'event');

  const headersLength = h1.length + h2.length + h3.length;
  const headers = new Uint8Array(headersLength);
  let hOff = 0;
  headers.set(h1, hOff); hOff += h1.length;
  headers.set(h2, hOff); hOff += h2.length;
  headers.set(h3, hOff);

  const payload = new Uint8Array(pcmBuffer);
  const totalLength = 4 + 4 + 4 + headersLength + payload.length + 4;

  const out = new Uint8Array(totalLength);
  const dv = new DataView(out.buffer);

  // Prelude: total_length + headers_length
  dv.setUint32(0, totalLength, false);
  dv.setUint32(4, headersLength, false);

  // Prelude CRC (covers first 8 bytes)
  const preludeCrc = crc32(out.slice(0, 8));
  dv.setUint32(8, preludeCrc, false);

  // Headers + payload
  out.set(headers, 12);
  out.set(payload, 12 + headersLength);

  // Message CRC (covers everything except the final 4-byte CRC itself)
  const messageCrc = crc32(out.slice(0, totalLength - 4));
  dv.setUint32(totalLength - 4, messageCrc, false);

  return out.buffer;
}

// ── parseTranscriptEvent ──────────────────────────────────────────────────────

export interface TranscriptResult {
  transcript: string;
  isPartial: boolean;
}

/**
 * Parse an incoming Transcribe event-stream binary frame.
 * Returns null if the frame is not a TranscriptEvent or has no results.
 *
 * Incoming frame payload is JSON — extract the Transcript.Results array.
 */
export function parseTranscriptEvent(data: ArrayBuffer): TranscriptResult | null {
  try {
    const view = new DataView(data);
    const totalLength = view.getUint32(0, false);
    const headersLength = view.getUint32(4, false);
    // Skip prelude CRC (4 bytes) + headers
    const payloadOffset = 12 + headersLength;
    const payloadLength = totalLength - payloadOffset - 4; // minus trailing CRC
    if (payloadLength <= 0) return null;

    const payloadBytes = new Uint8Array(data, payloadOffset, payloadLength);
    const payloadText = new TextDecoder().decode(payloadBytes);

    // Parse the JSON payload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsed = JSON.parse(payloadText) as any;
    const results = parsed?.Transcript?.Results;
    if (!Array.isArray(results) || results.length === 0) return null;

    const result = results[0];
    const alternatives = result?.Alternatives;
    if (!Array.isArray(alternatives) || alternatives.length === 0) return null;

    const transcript = (alternatives[0]?.Transcript as string) ?? '';
    const isPartial = result?.IsPartial === true;

    return { transcript, isPartial };
  } catch {
    return null;
  }
}
